import React from 'react';
import { motion } from 'framer-motion';
import { FileX, DollarSign, AlertTriangle } from 'lucide-react';
import SectionWrapper from '../shared/SectionWrapper';

const problems = [
  {
    icon: FileX,
    title: 'Manual Data Chaos',
    description: '100+ hours per cycle collecting data across departments with error-prone spreadsheets',
    accent: '#f43f5e',
    bg: 'bg-rose-50 dark:bg-rose-900/10',
    border: 'border-rose-100 dark:border-rose-800/30',
    iconBg: 'bg-rose-100 dark:bg-rose-900/40',
    iconColor: 'text-rose-500',
  },
  {
    icon: DollarSign,
    title: 'Hidden Compliance Costs',
    description: '$250K+ in consulting fees and regulatory penalties from incomplete reports',
    accent: '#f59e0b',
    bg: 'bg-amber-50 dark:bg-amber-900/10',
    border: 'border-amber-100 dark:border-amber-800/30',
    iconBg: 'bg-amber-100 dark:bg-amber-900/40',
    iconColor: 'text-amber-500',
  },
  {
    icon: AlertTriangle,
    title: 'Regulatory Complexity',
    description: '35+ evolving ESG frameworks across regions — impossible to track manually',
    accent: '#f97316',
    bg: 'bg-orange-50 dark:bg-orange-900/10',
    border: 'border-orange-100 dark:border-orange-800/30',
    iconBg: 'bg-orange-100 dark:bg-orange-900/40',
    iconColor: 'text-orange-500',
  },
];

const ProblemSection = () => (
  <SectionWrapper id="problem" bgClass="bg-white dark:bg-[#0f172a]" padding="py-10 md:py-12">
    <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-16">

      {/* Left — headline + subtitle */}
      <motion.div
        className="lg:w-80 flex-shrink-0"
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <h2 className="text-3xl md:text-4xl lg:text-[2.8rem] font-extrabold tracking-tight text-gray-900 dark:text-white leading-tight">
          ESG Compliance<br />
          <span className="text-rose-500">Shouldn't Feel</span><br />
          Like This
        </h2>
        <p className="mt-3 text-sm text-gray-500 dark:text-slate-400 leading-relaxed">
          80% of enterprises still rely on spreadsheets for ESG reporting. The result?
        </p>
        <p className="mt-4 text-sm font-semibold text-blue-600 dark:text-blue-400">
          There's a better way. →
        </p>
      </motion.div>

      {/* Right — problem cards stacked compactly */}
      <div className="flex-1 flex flex-col gap-3">
        {problems.map((p, i) => {
          const Icon = p.icon;
          return (
            <motion.div
              key={p.title}
              className={`flex items-start gap-4 rounded-xl border px-5 py-4 ${p.bg} ${p.border}`}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.45, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${p.iconBg}`}>
                <Icon className={`w-4.5 h-4.5 ${p.iconColor}`} strokeWidth={1.8} />
              </div>
              <div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">{p.title}</div>
                <div className="mt-0.5 text-xs text-gray-500 dark:text-slate-400 leading-relaxed">{p.description}</div>
              </div>
            </motion.div>
          );
        })}
      </div>

    </div>
  </SectionWrapper>
);

export default ProblemSection;
